import { useState, useCallback } from "react";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import CrystalBallHero from "@/components/crystal-ball/CrystalBallHero";
import CrystalBallUpload from "@/components/crystal-ball/CrystalBallUpload";
import CrystalBallAnalysis from "@/components/crystal-ball/CrystalBallAnalysis";
import CrystalBallResults from "@/components/crystal-ball/CrystalBallResults";
import CrystalBallROI from "@/components/crystal-ball/CrystalBallROI";
import ScheduleModal from "@/components/crystal-ball/ScheduleModal";

type DemoStep = "upload" | "analyzing" | "results";

export default function QualityCrystalBall() {
  const [step, setStep] = useState<DemoStep>("upload");
  const [isUploaded, setIsUploaded] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const handleUpload = () => {
    setIsUploaded(true);
    setTimeout(() => setStep("analyzing"), 800);
  };

  const handleAnalysisComplete = useCallback(() => {
    setStep("results");
  }, []);

  const handleDownloadReport = () => {
    const reportContent = `
QUALITY CRYSTAL BALL, DEFECT PREDICTION ANALYSIS REPORT
Generated: ${new Date().toLocaleString()}

=================================================================
EXECUTIVE SUMMARY
=================================================================
Total Predicted Defects (Next 7 Days): 23
Preventable Defects: 18 (78%)
Weekly Savings Potential: $47,300
Annual ROI: $1,920,000

=================================================================
DETAILED PREDICTIONS
=================================================================
1. Tue Feb 11, 2:47 PM | Line 3 | Temperature Variance | 94% | $4,200
   → Recalibrate temperature sensor on Unit 7B before 1:00 PM

2. Tue Feb 11, 4:15 PM | Line 2 | Material Thickness | 89% | $2,800
   → Adjust feed rate by 3.2% and verify material batch #4471

3. Wed Feb 12, 9:23 AM | Line 1 | Pressure Drop | 78% | $3,900
   → Schedule filter cleaning during morning break

4. Wed Feb 12, 1:30 PM | Line 3 | Vibration Anomaly | 85% | $5,100
   → Replace bearing assembly #12, 2hr maintenance window

5. Thu Feb 13, 10:45 AM | Line 2 | Humidity Drift | 72% | $1,900
   → Activate backup dehumidifier in Zone B

6. Thu Feb 13, 3:00 PM | Line 1 | Alignment Shift | 91% | $6,200
   → Run auto-calibration sequence before afternoon shift

7. Fri Feb 14, 8:15 AM | Line 3 | Coolant Level | 67% | $2,100
   → Top off coolant reservoir, scheduled check

8. Fri Feb 14, 11:30 AM | Line 2 | Motor Current Spike | 88% | $4,800
   → Inspect motor controller firmware for Line 2

=================================================================
ROI ANALYSIS
=================================================================
Current Annual Defect Costs:  $2,400,000
Projected Annual Savings:     $1,920,000
Implementation Payback:       2.3 months
5-Year Net Impact:            $9,100,000

=================================================================
For implementation inquiries: Arqera AI
    `.trim();

    const blob = new Blob([reportContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Quality_Crystal_Ball_Analysis_Report.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Quality Crystal Ball | AI Defect Prediction Demo | ArqeraAI"
        description="Interactive demo: see how AI predicts manufacturing defects before they ship. Upload sample data, get a quality forecast, and quantify the ROI in minutes."
        canonical="https://arqeraai.com/quality-crystal-ball"
        ogTitle="Quality Crystal Ball: AI Defect Prediction Demo"
        ogDescription="See how AI catches manufacturing defects before they ship. Live demo with ROI calculation."
      />
      {/* Top Nav */}
      <nav className="sticky top-0 z-40 border-b border-crystal-purple/10 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
          <Link to="/" className="flex items-center gap-2 text-medium-gray hover:text-foreground transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Arqera AI
          </Link>
          <span className="text-sm font-semibold text-foreground">⚡ Quality Crystal Ball</span>
        </div>
      </nav>

      <CrystalBallHero />

      {/* 3-step flow */}
      {step === "upload" && (
        <CrystalBallUpload onUpload={handleUpload} isUploaded={isUploaded} />
      )}

      {step === "analyzing" && (
        <CrystalBallAnalysis onComplete={handleAnalysisComplete} />
      )}

      {step === "results" && (
        <>
          <CrystalBallResults
            onSchedule={() => window.open("https://calendly.com/hello-arqeraai/30min", "_blank", "noopener,noreferrer")}
            onDownload={handleDownloadReport}
          />
          <CrystalBallROI />

          {/* Bottom CTA */}
          <section className="py-16 px-6">
            <div className="max-w-3xl mx-auto text-center rounded-2xl border border-crystal-purple/20 bg-gradient-to-br from-crystal-purple/10 to-crystal-blue/10 p-8 md:p-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                Ready to Prevent $1.9M in Defects?
              </h2>
              <p className="text-medium-gray mb-6 max-w-xl mx-auto">
                Join leading manufacturers who use Arqera AI's Quality Crystal Ball
                to eliminate defects before they happen.
              </p>
              <a
                href="https://calendly.com/hello-arqeraai/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-lg bg-gradient-to-r from-crystal-purple to-crystal-blue text-foreground font-semibold hover:opacity-90 transition-opacity"
              >
                Schedule Implementation Call
              </a>
            </div>
          </section>
        </>
      )}

      {/* Footer */}
      <footer className="border-t border-crystal-purple/10 py-8 px-6 text-center">
        <p className="text-medium-gray text-sm">
          © {new Date().getFullYear()} Arqera AI. All rights reserved. ⚡ Quality Crystal Ball is a product of Arqera AI.
        </p>
        <p className="text-medium-gray/60 text-xs mt-2">
          Demo data is simulated for illustrative purposes. Actual results depend on your production environment.
        </p>
      </footer>

      <ScheduleModal open={showScheduleModal} onClose={() => setShowScheduleModal(false)} />
    </div>
  );
}
